import React from 'react';
import { Onboarding } from '@/features/onboarding/components/primitives';
import { FactorType } from '@/features/onboarding/types/onboarding.types';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Context passed to all factor renderer components
 * This unified interface allows any factor to work in any position (primary/secondary)
 */
export interface FactorRenderContext {
  // Factor info
  factorType: FactorType;
  metadata?: Record<string, unknown>;

  // UI state
  isLoading: boolean;
  error: string | null;

  // Retry state - when max retries exceeded, inputs should be disabled
  hasExceededRetries?: boolean;

  // Callbacks
  onSubmit: (payload: Record<string, unknown>) => Promise<boolean>;
  onClearError: () => void;

  // OTP-specific (optional - only used by OTP-based components)
  resendCountdown?: number;
  resendCount?: number;
  canResend?: boolean;
  onResend?: () => Promise<boolean>;

  // Back navigation (shown when currentStep && hasAlternative)
  showBackButton?: boolean;
  onBack?: () => void;

  // Lets factor steps branch on mobile-onboarding eligibility (e.g. encode the
  // invitation URL in the QR so a phone camera can deep-link).
  isMobileOnboardingEligible?: boolean;

  // Authoritative tenant slug from the validation response. Preferred over
  // `useParams().tenant` because the legacy `/onboarding/:token` route is
  // tenant-less and would otherwise degrade mobile-eligible QR payloads.
  tenantSlug?: string;
}

export type FactorRenderer = React.FC<FactorRenderContext>;

// ============================================================================
// LAZY IMPORTS - Components loaded on demand
// ============================================================================

// We use lazy imports to avoid circular dependencies and improve code splitting
const PasswordSetupStep = React.lazy(() =>
  import('./steps/password-setup-step').then(m => ({
    default: m.PasswordSetupStep,
  }))
);

const OtpVerificationStep = React.lazy(() =>
  import('./steps/otp-verification-step').then(m => ({
    default: m.OtpVerificationStep,
  }))
);

const TotpSetupStep = React.lazy(() =>
  import('./steps/totp-setup-step').then(m => ({ default: m.TotpSetupStep }))
);

const WebauthnSetupStep = React.lazy(() =>
  import('./steps/webauthn-setup-step').then(m => ({
    default: m.WebauthnSetupStep,
  }))
);

const PushNotificationStep = React.lazy(() =>
  import('./steps/push-notification-step').then(m => ({
    default: m.PushNotificationStep,
  }))
);

// ============================================================================
// FACTOR REGISTRY
// ============================================================================

/**
 * Registry mapping FactorType to its renderer component
 * Add new factors here - they'll work in ANY position (primary/secondary)
 *
 * To add a new factor:
 * 1. Create the component in ./steps/
 * 2. Add lazy import above
 * 3. Add entry to this registry
 */
const FACTOR_RENDERERS: Record<
  FactorType,
  React.LazyExoticComponent<FactorRenderer>
> = {
  [FactorType.PASSWORD]: PasswordSetupStep,
  [FactorType.SMS_OTP]: OtpVerificationStep,
  [FactorType.WHATSAPP_OTP]: OtpVerificationStep,
  [FactorType.TOTP]: TotpSetupStep,
  [FactorType.WEBAUTHN]: WebauthnSetupStep,
  [FactorType.PUSH_NOTIFICATION]: PushNotificationStep,
};

// ============================================================================
// MAIN RENDERER COMPONENT
// ============================================================================

/**
 * FactorStepRenderer - Dynamically renders the appropriate factor component
 *
 * Uses React.Suspense for lazy loading with a loading fallback.
 * Throws error for unknown factor types - this indicates a bug that must be fixed.
 */
export const FactorStepRenderer: React.FC<FactorRenderContext> = props => {
  const { factorType } = props;
  const LazyComponent = FACTOR_RENDERERS[factorType];

  if (!LazyComponent) {
    throw new Error(
      `[FactorStepRenderer] Unknown factor type: "${factorType}". ` +
        `This is a bug - either backend sent an invalid factor or frontend is missing a renderer. ` +
        `Registered types: ${Object.keys(FACTOR_RENDERERS).join(', ')}`
    );
  }

  return (
    <React.Suspense fallback={<Onboarding.Loading />}>
      <LazyComponent {...props} />
    </React.Suspense>
  );
};
